import { nothing, html, LitElement, css } from "lit";
import { ifDefined } from "lit-html/directives/if-defined.js";
import { SignalWatcher } from "@lit-labs/preact-signals";
import { BaseComponent } from "../../BaseComponent.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { rif } from "../../../helpers/renderIf.js";
import "../../atom/ModalBase/index.js";
import "../../atom/ClickOutside/index.js";
import "../Button/index.js";
import "../IconButton/index.js";
import { testingAttr } from "../../../directives/testingAttr.js";
import { TestingAttrs } from "./modal.selectors.js";
import { isMobile, breakpoints } from "../../../responsive/index.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let DsfModal = class extends SignalWatcher(BaseComponent) {
  constructor() {
    super();
    this.dsf_focus_selector = "input";
  }
  render() {
    if (!this.dsf_open) {
      return nothing;
    }
    const title = rif(
      this.dsf_title,
      html`<p class="dsf-text-m-semibold mobile:dsf-text-s-semibold">${this.dsf_title}</p> `
    );
    const description = rif(
      this.dsf_description,
      html` <p class="dsf-text-xs">${this.dsf_description}</p> `
    );
    const closeIcon = rif(
      !this.dsf_hide_close_icon,
      html`<dsf-icon-button
        @click=${() => this.emit("close")}
        dsf_icon="X"
        .disabled=${this.disabled}
      ></dsf-icon-button>`
    );
    const closeButton = rif(
      this.dsf_close_text,
      html` <dsf-button
        @click=${() => this.emit("close")}
        dsf_level="secondary"
        .disabled=${this.disabled}
        dsf_size=${isMobile() ? "small" : "medium"}
        ${testingAttr(TestingAttrs.closeButton)}
      >
        ${this.dsf_close_text}
      </dsf-button>`
    );
    const secondaryButton = rif(
      this.dsf_cancel_text,
      html` <dsf-button
        @click=${() => this.emit("cancel")}
        dsf_level="secondary"
        .disabled=${this.disabled || this.dsf_loading}
        dsf_size=${isMobile() ? "small" : "medium"}
        ${testingAttr(TestingAttrs.cancelButton)}
      >
        ${this.dsf_cancel_text}
      </dsf-button>`
    );
    const primaryButton = rif(
      this.dsf_confirm_text,
      html` <dsf-button
        @click=${() => this.emit("confirm")}
        .disabled=${this.disabled}
        .dsf_loading=${this.dsf_loading}
        dsf_size=${isMobile() ? "small" : "medium"}
        ${testingAttr(TestingAttrs.confirmButton)}
      >
        ${this.dsf_confirm_text}
      </dsf-button>`
    );
    const footer = rif(
      this.dsf_close_text || this.dsf_cancel_text || this.dsf_confirm_text,
      html`
        <div
          slot="footer"
          class="flex shrink-0 justify-between border-t border-t-neutral-100 px-16 py-16 mobile:py-12"
        >
          ${closeButton}
          <div class="flex w-full justify-end gap-10">${secondaryButton} ${primaryButton}</div>
        </div>
      `
    );
    return html`
      <dsf-modal-base
        exportparts="overlay, modal"
        dsf_focus_selector=${ifDefined(this.dsf_focus_selector)}
      >
        <div
          slot="header"
          class="flex min-h-60 shrink-0 flex-col justify-center border-b border-b-neutral-100 py-12 pl-16 pr-8 mobile:min-h-48 mobile:py-6"
        >
          <div class="flex items-center justify-between">${title} ${closeIcon}</div>
          ${description}
        </div>
        <div slot="content" part="content" class="flex-auto overflow-auto p-16">
          <slot></slot>
        </div>
        ${footer}
      </dsf-modal-base>
    `;
  }
};
DsfModal.shadowRootOptions = {
  ...LitElement.shadowRootOptions,
  delegatesFocus: false
};
DsfModal.properties = {
  dsf_description: { type: String },
  dsf_title: { type: String },
  dsf_close_text: { type: String },
  dsf_confirm_text: { type: String },
  dsf_cancel_text: { type: String },
  dsf_focus_selector: { type: String },
  dsf_open: { type: Boolean, reflect: true },
  dsf_loading: { type: Boolean, reflect: true },
  disabled: { type: Boolean, reflect: true },
  dsf_hide_close_icon: { type: Boolean, reflect: true }
};
DsfModal.styles = [
  ...BaseComponent.styles,
  css`
      @media only screen and (min-width: ${breakpoints.tablet}px) {
        ::part(modal) {
          min-width: 520px;
        }
      }
    `
];
DsfModal = __decorateClass([
  defineElement("dsf-modal")
], DsfModal);
export {
  DsfModal
};
